import Network from 'infrastructure/js/modules/network';
import { createLabelHelper } from 'infrastructure/js/utils/labelHelper';

/**
 * Shared reading of the server's SapVerificationResponseModel (SAAS-24979).
 *
 * Two consumers, with different wording and different label namespaces: the Import Cut Plan
 * wizard's Step 2 grid, and the manual Report Cut dialog (SAAS-25063). What they share - and the
 * only reason this is extracted - is the response shape and the *order the branches have to be
 * taken in*. Getting that order wrong is how "we could not check" quietly turns into "it is fine",
 * which is the one outcome the server side of this feature was built to prevent.
 *
 * Wording stays with the caller: every label key is passed in, none is hardcoded here.
 */

const SEVERITY_SUCCESS = 'SUCCESS';


/**
 * True only when the call completed AND every submitted item passed.
 *
 * A transport failure is not a pass. Both callers gate their action on this, so neither can
 * accidentally read a dropped connection as a clean verification.
 */
export function isVerified(response) {
  return Network.isResponseValid(response) && !!response?.data?.allValid;
}

/**
 * The items that did not pass, in the order the server sorted them (heaviest severity first).
 *
 * Exported because a caller may want to do more with them than render a sentence - the Report Cut
 * dialog maps them back to the row they came from. Note the server sorts by severity, so these are
 * NOT index-aligned with the request; correlate by the values echoed on each item.
 */
export function getFailedItems(response) {
  return (response?.data?.logContentItems || []).filter((item) => item.severity !== SEVERITY_SUCCESS);
}

/**
 * Turns a failed verification into a single message for a dialog's error area.
 *
 * @param response            the raw network response
 * @param options.itemLabelPrefix     namespace for the per-item operation-status label keys
 * @param options.fallbackLabelKey    fully-qualified key: verification failed, but no item said why
 * @param options.notAvailableLabelKey fully-qualified key: nothing was verified at all
 * @param options.formatLine          optional (item, text) => string, to prefix each line with
 *                                    something only the caller knows about - e.g. which roll
 */
export function buildFailureMessage(response, options) {

  const { itemLabelPrefix, fallbackLabelKey, notAvailableLabelKey, formatLine } = options;

  // Fully-qualified keys are resolved with an empty prefix, unlike the per-item ones below.
  // Deliberate: these messages are not operation statuses, so they do not belong under that
  // namespace, and the full key stays greppable.
  const fullKeyLabels = createLabelHelper('');

  // Nothing came back at all (HTTP error, dropped connection). Same user-facing situation as the
  // server's own generalError below - nothing was verified - so say that, and not the fallback,
  // which would claim a mismatch with SAP data we never got far enough to observe.
  if (!Network.isResponseValid(response)) {
    return { message: fullKeyLabels.get(notAvailableLabelKey), statusType: 'error' };
  }

  // The call itself failed (source unreachable, misconfigured, rejected our credentials) - again,
  // nothing was verified. The server has already reduced every technical status to one sentence
  // the operator can act on; the status, url and remote wording stay in the call log.
  const generalError = response.data?.generalError;

  if (generalError) {
    return {
      message: fullKeyLabels.get(generalError.labelKey, undefined, generalError.params),
      statusType: 'error',
    };
  }

  const itemLabels = createLabelHelper(itemLabelPrefix);

  const failedLines = getFailedItems(response).map((item) => {

    const text = (item.operationStatus || [])
      .map((status) => itemLabels.get(status.labelKey, undefined, status.params))
      .join(' ');

    return formatLine ? formatLine(item, text) : text;
  });

  // Verification failed, yet no item says why. Nothing precise can be offered, so say the generic
  // sentence rather than showing an empty error box.
  if (failedLines.length === 0) {
    return { message: fullKeyLabels.get(fallbackLabelKey), statusType: 'error' };
  }

  return { message: failedLines.join('\n'), statusType: 'error' };
}

/**
 * The same reading of the response as {@link buildFailureMessage}, but structured: one entry per
 * thing that went wrong, in the `{errorKey, params}` shape a dialog's native validation area
 * consumes (see dialogHelper.getValidationArea).
 *
 * Two forms rather than one because the two consumers display failures differently, not because
 * they disagree about what the response means. The wizard's Step 2 grid has no error area of its
 * own and collects everything into one bottom-of-dialog string; the Report Cut dialog already has
 * a validation area that renders an icon and a line per entry, which is strictly better when the
 * rolls in the dialog are unrelated to each other. The branch *order* - the part worth sharing -
 * is identical in both.
 *
 * Every key returned is resolved by the caller under one prefix, so all of them must live there.
 *
 * @param response                       the raw network response
 * @param options.notAvailableErrorKey   key for "nothing was verified at all"
 * @param options.fallbackErrorKey       key for "verification failed, but no item said why"
 * @param options.decorateParams         optional (item, params) => params, to add values only the
 *                                       caller knows - e.g. which roll the item came from
 */
export function buildFailureEntries(response, options) {

  const { notAvailableErrorKey, fallbackErrorKey, decorateParams } = options;

  // Transport failure and the server's own generalError are one situation to an operator: nothing
  // was verified. Deliberately NOT resolved through the server's generalError.labelKey, unlike
  // buildFailureMessage - that key is hardcoded to the wizard's namespace
  // (SapVerificationServiceImpl.LABEL_GENERAL_ERROR) and would not resolve under this caller's
  // prefix, nor read right if it did. It is also the only value the server ever sets there, so
  // nothing is lost by not reading it: its presence is the whole message.
  if (!Network.isResponseValid(response) || response.data?.generalError) {
    return [{ errorKey: notAvailableErrorKey }];
  }

  const entries = [];

  getFailedItems(response).forEach((item) => {
    (item.operationStatus || []).forEach((status) => {
      entries.push({
        errorKey: status.labelKey,
        params: decorateParams ? decorateParams(item, status.params) : status.params,
      });
    });
  });

  // Verification failed, yet no item says why. Nothing precise can be offered, so say the generic
  // sentence rather than showing an empty error box.
  if (entries.length === 0) {
    return [{ errorKey: fallbackErrorKey }];
  }

  return entries;
}
